'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { hasDeadlineExpired } from '../../../utils/Sunbasket';
import AuthStore from '../../../stores/AuthStore';
import { getConfig } from '../../../utils/Env';

import './DocumentType.scss';

export default class DocumentTypeParams extends Component {
    static propTypes = {
        allowedTypes: PropTypes.array,
        setLoading: PropTypes.func
    };

    static contextTypes = {
        user: PropTypes.object,
        hideProductTypeButtons: PropTypes.bool,
        isPro: PropTypes.bool,
        addSwapContext: PropTypes.object
    };

    constructor(props) {
        super(props);

        this.state = {
            availableSunbasketOnly: false,
        };
    }

    onClickAll = () => {
        const { params, allowedTypes, onChangeParams } = this.props;

        params.types = allowedTypes.slice();
        params.filters = params.filters || {};
        delete params.filters.product_type;
        delete params.filters.is_owner;

        onChangeParams(params);
    }

    onClickRecipes = () => {
        const { params, allowedTypes, onChangeParams } = this.props;

        params.types = ['recipe'];
        params.filters = params.filters || {};
        delete params.filters.product_type;
        delete params.filters.is_owner;

        onChangeParams(params);
    }

    onClickCustomFoodsAndRecipes = () => {
        const { params, allowedTypes, onChangeParams } = this.props;

        params.types = ['recipe', 'food'];
        params.filters = params.filters || {};
        params.filters.is_owner = true;

        delete params.filters.product_type;

        onChangeParams(params);
    }

    onClickCollections = () => {
        const { params, allowedTypes, onChangeParams } = this.props;

        params.types = ['collection'];
        params.filters = params.filters || {};
        delete params.filters.product_type;
        delete params.filters.is_owner;

        onChangeParams(params);
    }

    onClickMealPlans = () => {
        const { params, allowedTypes, onChangeParams } = this.props;

        params.types = ['plan'];
        params.filters = params.filters || {};
        delete params.filters.product_type;
        delete params.filters.is_owner;

        onChangeParams(params);
    }

    onClickFoods = () => {
        const { params, allowedTypes, onChangeParams } = this.props;

        params.types = ['food'];
        params.filters = params.filters || {};
        delete params.filters.product_type;
        delete params.filters.is_owner;

        onChangeParams(params);
    }

    onClickProductType = (product_type) => {
        const { onChangeParams } = this.props;
        let { params } = this.props;

        params.types = ['food'];
        params.filters = params.filters || {};
        params.filters.product_type = product_type;
        delete params.filters.is_owner;

        onChangeParams(params);
    }

    onClickRestaurants = () => {
        this.onClickProductType('Restaurant Dish');
    }

    onClickMealKits = () => {
        this.onClickProductType('Meal Kit');
    }

    onClickReadyMadeMeals = () => {
        this.onClickProductType('Ready Made Meal');
    }

    appendSunbasketParams = async (params) => {
        const { addSwapContext , user } = this.context;
        const { postal_code } = user

        const searchDate = moment(addSwapContext.date);

        const deliveryInfo = await AuthStore.fetch(
            getConfig('recipe_api') + '/sunbasket/zipcode-delivery-info?' + [
                'date=' + encodeURIComponent(addSwapContext.date.format('YYYY-MM-DD')),
                'postalCode=' + encodeURIComponent(postal_code),
            ].join('&')
        );

        params.filters.store.push('sunbasket');
        params.filters.available_on = [
            'always',
            searchDate.format('YYYY-MM-DD'),
            searchDate.clone().subtract(1, 'day').format('YYYY-MM-DD'),
            searchDate.clone().subtract(2, 'day').format('YYYY-MM-DD'),
            searchDate.clone().subtract(3, 'day').format('YYYY-MM-DD'),
            searchDate.clone().subtract(4, 'day').format('YYYY-MM-DD'),
        ];

        params.filters.available_on = params.filters.available_on.filter((date)=> !hasDeadlineExpired(date));

        // Don't recommend us stuff that's not available
        params.filters['!food_uuid'] = (params.filters['!food_uuid'] || []).concat(deliveryInfo?.unavailable || []);

        return params;
    }

    toggleSunbasketFilter = () => {
        const { user, isPro } = this.context;
        const { onChangeParams, setLoading } = this.props;
        let { params } = this.props;

        this.setState(
            prevState => ({
                availableSunbasketOnly: !prevState.availableSunbasketOnly
            }),
            async () => {
                const { availableSunbasketOnly } = this.state;

                if (availableSunbasketOnly) {
                    setLoading(true);
                    params.filters.store = ['universal'];

                    if (!isPro) {
                        for (const provider of user?.preferences?.meal_kit_providers || []) {
                            if (provider === 'sunbasket') {
                                params = await this.appendSunbasketParams(params);
                            }
                            // @todo add more providers here later
                        }
                    }

                    onChangeParams(params);
                } else {
                    if (Array.isArray(params.filters.store)) {
                        params.filters.store = params.filters.store.filter(store => store !== 'sunbasket');

                        if (params.filters.store.length === 0) {
                            delete params.filters.store;
                        }
                    }

                    delete params.filters.available_on;

                    delete params.filters['!food_uuid'];

                    onChangeParams(params);
                }
            }
        );
    };

    render() {
        const { user, hideProductTypeButtons, isPro } = this.context;
        const { params, allowedTypes, hasSunbasketFood } = this.props;
        const { availableSunbasketOnly } = this.state;
        const { types = [], filters = {} } = params;

        // If we're only allowing foods & recipes, don't show the interface.
        if (!(allowedTypes.includes('food'))) {
            return <></>
        }

        let isAllSelected = types.length === allowedTypes.length && !filters.product_type && !filters.is_owner;

        let isRecipesSelected = types.includes('recipe') && types.length === 1 && !filters.product_type && !filters.is_owner;
        let isCustomFoodsAndRecipesSelected =
            types.includes('recipe') && types.includes('food') && types.length === 2 && filters.is_owner;
        let isCollectionsSelected = types.includes('collection') && types.length === 1 && !filters.product_type && !filters.is_owner;
        let isMealPlansSelected = types.includes('plan') && types.length === 1 && !filters.product_type && !filters.is_owner;

        let isFoodsSelected = types.includes('food') && types.length === 1 && !filters.product_type && !filters.is_owner;

        let isRestaurantSelected = types.includes('food') && types.length === 1 &&
                                   filters.product_type === 'Restaurant Dish' && !filters.is_owner;

        let isMealKitsSelected = types.includes('food') && types.length === 1 &&
                                   filters.product_type === 'Meal Kit' && !filters.is_owner;

        let isReadyMadeMealsSelected = types.includes('food') && types.length === 1 &&
                                   filters.product_type === 'Ready Made Meal' && !filters.is_owner;

        return (
            <div className="doc-type-params">
                <ul>
                    <li className="all">
                        <button onClick={this.onClickAll} data-active={isAllSelected}>All</button>
                    </li>
                    {allowedTypes.includes('recipe') && allowedTypes.includes('food') ?
                        <li className="recipes" onClick={this.onClickCustomFoodsAndRecipes}>
                            <button data-active={isCustomFoodsAndRecipesSelected}>My Custom Foods and Recipes</button>
                        </li>
                    : null}
                    {allowedTypes.includes('recipe') ?
                        <li className="recipes" onClick={this.onClickRecipes}>
                            <button data-active={isRecipesSelected}>Recipes</button>
                        </li>
                    : null}
                    {allowedTypes.includes('collection') ?
                        <li className="collections" onClick={this.onClickCollections}>
                            <button data-active={isCollectionsSelected}>Collections</button>
                        </li>
                    : null}
                    {allowedTypes.includes('plan') ?
                        <li className="plans" onClick={this.onClickMealPlans}>
                            <button data-active={isMealPlansSelected}>Meal Plans</button>
                        </li>
                    : null}
                    {allowedTypes.includes('food') ?
                        <li className="foods">
                            <button data-active={isFoodsSelected} onClick={this.onClickFoods}>Foods</button>
                        </li>
                    : null}
                    {allowedTypes.includes('food') && !hideProductTypeButtons ?
                        <li>
                            <button data-active={isRestaurantSelected} onClick={this.onClickRestaurants}>Restaurant Meals</button>
                        </li>
                    : null}
                    {allowedTypes.includes('food') && !hideProductTypeButtons && user?.preferences?.meal_kit_providers?.includes('sunbasket') && user?.postal_code ?
                        <li>
                            <button data-active={isMealKitsSelected} onClick={this.onClickMealKits}>Meal Kits</button>
                        </li>
                    : null}
                    {allowedTypes.includes('food') && !hideProductTypeButtons && user?.preferences?.meal_kit_providers?.includes('sunbasket') && user?.postal_code ?
                        <li>
                            <button data-active={isReadyMadeMealsSelected} onClick={this.onClickReadyMadeMeals}>Ready Made Meals</button>
                        </li>
                    : null}
                </ul>
                {availableSunbasketOnly || (hasSunbasketFood && !isPro) ?
                    <button class="el-checkbox" data-active={availableSunbasketOnly} onClick={this.toggleSunbasketFilter} >Only show meals currently available for ordering</button>
                : null}
            </div>
        );
    }
}
